import { inject, Injectable, InjectionToken, signal } from '@angular/core';

/** When false, theme stays light and any stored preference is cleared. */
export const THEME_TOGGLE_ENABLED = new InjectionToken<boolean>(
  'THEME_TOGGLE_ENABLED',
  { providedIn: 'root', factory: () => true },
);

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  readonly isDark = signal(false);

  private readonly _toggleEnabled = inject(THEME_TOGGLE_ENABLED);

  private readonly THEME_COLORS = {
    dark: '#0c0e12',
    light: '#ffffff',
  };

  constructor() {
    // Theme toggle is disabled in production; drop any leftover preference
    // so users who previously chose dark are forced back to light.
    if (!this._toggleEnabled) {
      localStorage.removeItem('theme');
      this.isDark.set(false);
    } else {
      this.isDark.set(localStorage.getItem('theme') === 'dark');
    }

    document.body.setAttribute('data-theme', this.isDark() ? 'dark' : 'light');
    this._updateThemeColor();
  }

  toggle(): void {
    if (!this._toggleEnabled) {
      return;
    }

    this.isDark.update((v) => !v);
    const theme = this.isDark() ? 'dark' : 'light';
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    this._updateThemeColor();
  }

  private _updateThemeColor(): void {
    const color = this.isDark()
      ? this.THEME_COLORS.dark
      : this.THEME_COLORS.light;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', color);
  }
}
